// src/pages/MatchSetup.js
import React, { useState, useCallback, useMemo, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { useTheme } from "../config/ThemeContext";
import apiClient from "../services/apiClient";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const fetcher = (url) => apiClient.get(url).then(res => res.data);

// Lazy-loaded components
const EditMatchDialog = React.lazy(() => import("../components/EditMatchDialog"));
const CreateMatchForm = React.lazy(() => import("../components/CreateMatchForm"));
const ExcelUpload = React.lazy(() => import("../components/ExcelUpload"));

// Memoized option for team select
const TeamOption = React.memo(({ team }) => (
  <option key={team.id} value={team.id}>{team.name}</option>
));

// Memoized row for displaying a match
const MatchRow = React.memo(({ match, onEdit, onDelete, onStart, onP2, onP3, colors }) => (
<li className="flex justify-between items-center border-b py-2 last:border-0">
  <div>
    <div className={`font-medium  ${colors.neutral.text}`}>
      {new Date(match.startTime).toLocaleString("en-US", {
        month: "long",
        day: "numeric",
      })}{" "}
      - <b>{match.matchTitle}</b>
      <span
        className={`text-xs px-2 py-1 rounded-full ${
          match.status === "Finished"
            ? "bg-green-100 text-green-800"
            : match.status === "In Progress"
            ? "bg-blue-100 text-blue-800"
            : "bg-gray-100 text-gray-800"
        }`}
      >
        {match.status}
      </span>
    </div>
    <div className="text-sm text-gray-500">
      {new Date(match.startTime.replace(/Z$/, "")).toLocaleTimeString([], {
	  hour: "2-digit",
	  minute: "2-digit",
	})}
    </div>
  </div>
  <div className="flex space-x-2">

    {match.status !== "Finished" ? (
      <>
	          <button
          onClick={() => onDelete(match.id)}
          className={`${colors.state.danger.text} ${colors.state.danger.border} ${colors.state.danger.hover} px-2 py-1 rounded`}
        >
          🗑️
        </button>
		    <button onClick={() => onEdit(match)} className={colors.buttons.secondary}>
      ✏️
    </button>

        <button
          onClick={() => onStart(match.id)}
          className={colors.buttons.primary}
        >
          🏐 Start Match
        </button>
      </>
    ) : (
      <>
	      <button onClick={() => onEdit(match)} className={colors.buttons.secondary}>
      ✏️
    </button>

        <button
          title="P2 Report"
          onClick={() => onP2(match.id)}
          className={colors.buttons.secondary}
        >
          📋 P2
        </button>
        <button
          title="P3 Skill"
          onClick={() => onP3(match.id)}
          className={colors.buttons.secondary}
        >
          ⭐ P3
        </button>
      </>
    )}
  </div>
</li>

));

export default function MatchSetup() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const colors = useTheme();

// ✅ Fetch season name
const { data: seasonData, error: seasonErr } = useSWR(
  seasonId ? `/api/seasons/${seasonId}` : null,
  fetcher
);
const seasonName = seasonData?.name || "";

// ✅ Fetch teams
const { data: rawTeams, error: teamsErr } = useSWR(
  seasonId ? `/api/teams/season/${seasonId}` : null,
  fetcher
);
const teams = Array.isArray(rawTeams) ? rawTeams : rawTeams?.teams || [];

// ✅ Fetch matches
const { data: matches, error: matchesErr } = useSWR(
  seasonId ? `/api/matches/season/${seasonId}?limit=100` : null,
  fetcher
);


  const loading = !seasonData || rawTeams === undefined || !matches;
  const error = seasonErr || teamsErr || matchesErr;

  // Memoize select options
  const teamOptions = useMemo(() => teams.map(t => <TeamOption team={t} key={t.id} />), [teams]);

  // Edit dialog state
  const [editingMatch, setEditingMatch] = useState(null);

  // Memoize match rows
  const matchRows = useMemo(() => (
    Array.isArray(matches) ? matches.map(m => (
      <MatchRow
        key={m.id}
        match={m}
        onEdit={(match) => {
			const latestMatch = matches?.find(m => m.id === match.id) || match;
			setEditingMatch(latestMatch);
		  }}
        onDelete={async (id) => {
          if (!window.confirm("Delete this match?")) return;
          await apiClient.delete(`/api/matches/${id}`);
	  	  mutate(`/api/matches/season/${seasonId}?limit=100`);
        }}
        onStart={(id) => navigate(`/matches/pre-match/${id}`)}
        onP2={(id) => window.open(`${API_BASE_URL}/api/matches/${id}/report`, "_blank")}
        onP3={(id) => window.open(`${API_BASE_URL}/api/matches/${id}/player-skill-report`, "_blank")}
        colors={colors}
      />
    )) : []
  ), [matches, colors, navigate, seasonId]);

  // Create match handler
  const handleCreateMatch = useCallback(
    async (formValues) => {
      const payload = { ...formValues, seasonId: Number(seasonId) };
      await apiClient.post(`/api/matches/create`, payload);
      mutate(`/api/matches/season/${seasonId}?limit=100`);
    },
    [seasonId]
  );
  
  const handleFileUpload = useCallback(async (file) => {
  if (!file) return;
  const formData = new FormData();
  formData.append("file", file);
  try {
    const res = await apiClient.post(
      `/api/matches/upload?seasonId=${seasonId}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    // show feedback…
    // Revalidate list
    mutate(`/api/matches/season/${seasonId}?limit=100`);
  } catch (err) {
    console.error(err);
    alert("Upload failed. Please check your file.");
  }
}, [seasonId]);


  if (loading) {
    return (
      <div className="p-4">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse" />
        <ul className="space-y-2">
          {[...Array(5)].map((_, i) => <li key={i} className="h-12 bg-gray-200 rounded animate-pulse" />)}
        </ul>
      </div>
    );
  }
  if (error) {
    return <div className="p-4 text-red-500">Failed to load data.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
<div className="text-center mb-8 relative">
  <h1 className={`text-3xl font-bold ${colors.neutral.text} mb-2`}>
    Matches for {seasonName || seasonId}
  </h1>
  <div className={`h-1 w-20 ${colors.primary.bg} mx-auto mt-4`}></div>

  {/* Download Button */}
  <div className="absolute right-0 top-0">
    <a
      href={`/schedule/${seasonId}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`${colors.buttons.secondary} inline-block px-4 py-2 rounded shadow`}
    >
      📄 View Schedule
    </a>
  </div>
</div>


	  <div className={`bg-white rounded-xl shadow-md p-6 my-6 border-t-4 ${colors.primary.border}`}>
	  	
      <ul className="space-y-4">{matchRows}</ul>
	  </div>

      {/* Create Match Form */}
      <Suspense fallback={<div className="p-4">Loading form…</div>}>
        <CreateMatchForm teams={teams} onCreate={handleCreateMatch} />
      </Suspense>
     <Suspense fallback={<div>Loading upload…</div>}>
		<ExcelUpload seasonId={seasonId} onUpload={handleFileUpload}/>
	</Suspense>

      {/* Edit Match Modal */}
      <Suspense fallback={<div className="p-4">Loading editor…</div>}>
        {editingMatch && (
          <EditMatchDialog
		  match={editingMatch}
		  teams={teams}
		  onSave={async (updated) => {
			await apiClient.put(`/api/matches/${updated.id}`, updated);
			// Update both the cache and revalidate
			mutate(`/api/matches/season/${seasonId}?limit=100`, (currentData) => {
			  return currentData.map(match => 
				match.id === updated.id ? updated : match
			  );
			}, true); // true = revalidate after updating
			setEditingMatch(null);
		  }}
		  onCancel={() => setEditingMatch(null)}
		/>
        )}
      </Suspense>
    </div>
  );
}
